import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

export interface ExportOptions {
  filename?: string;
  backgroundColor?: string;
  scale?: number;
}

/**
 * Export a DOM element as PNG image
 */
export async function exportAsPNG(
  element: HTMLElement,
  options: ExportOptions = {}
): Promise<void> {
  const {
    filename = 'chart-export.png',
    backgroundColor = '#ffffff',
    scale = 2,
  } = options;

  try {
    const canvas = await html2canvas(element, {
      backgroundColor,
      scale,
      useCORS: true,
      logging: false,
    });

    const link = document.createElement('a');
    link.download = filename;
    link.href = canvas.toDataURL('image/png');
    link.click();
  } catch (error) {
    console.error('Failed to export as PNG:', error);
    throw error;
  }
}

/**
 * Export a DOM element as PDF document
 */
export async function exportAsPDF(
  element: HTMLElement,
  options: ExportOptions = {}
): Promise<void> {
  const {
    filename = 'chart-export.pdf',
    backgroundColor = '#ffffff',
    scale = 2,
  } = options;

  try {
    const canvas = await html2canvas(element, {
      backgroundColor,
      scale,
      useCORS: true,
      logging: false,
    });

    const imgData = canvas.toDataURL('image/png');
    const imgWidth = canvas.width;
    const imgHeight = canvas.height;

    // Calculate PDF dimensions (A4 size in mm)
    const pdfWidth = 210;
    const pdfHeight = 297;
    const margin = 10;

    // Calculate the ratio to fit the image in PDF
    const ratio = Math.min(
      (pdfWidth - margin * 2) / imgWidth,
      (pdfHeight - margin * 2) / imgHeight
    );

    const scaledWidth = imgWidth * ratio;
    const scaledHeight = imgHeight * ratio;

    // Center the image
    const x = (pdfWidth - scaledWidth) / 2;
    const y = margin;

    const pdf = new jsPDF({
      orientation: scaledWidth > scaledHeight ? 'landscape' : 'portrait',
      unit: 'mm',
      format: 'a4',
    });

    pdf.addImage(imgData, 'PNG', x, y, scaledWidth, scaledHeight);
    pdf.save(filename);
  } catch (error) {
    console.error('Failed to export as PDF:', error);
    throw error;
  }
}

/**
 * Export all charts as a single PDF document
 */
export async function exportAllChartsAsPDF(
  elements: HTMLElement[],
  options: ExportOptions = {}
): Promise<void> {
  const {
    filename = 'learning-report.pdf',
    backgroundColor = '#ffffff',
    scale = 2,
  } = options;

  try {
    const pdf = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4',
    });

    const pageWidth = 297;
    const pageHeight = 210;
    const margin = 10;

    for (let i = 0; i < elements.length; i++) {
      const element = elements[i];

      if (i > 0) {
        pdf.addPage();
      }

      const canvas = await html2canvas(element, {
        backgroundColor,
        scale,
        useCORS: true,
        logging: false,
      });

      const imgData = canvas.toDataURL('image/png');
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;

      const ratio = Math.min(
        (pageWidth - margin * 2) / imgWidth,
        (pageHeight - margin * 2) / imgHeight
      );

      const scaledWidth = imgWidth * ratio;
      const scaledHeight = imgHeight * ratio;

      const x = (pageWidth - scaledWidth) / 2;
      const y = (pageHeight - scaledHeight) / 2;

      pdf.addImage(imgData, 'PNG', x, y, scaledWidth, scaledHeight);
    }

    pdf.save(filename);
  } catch (error) {
    console.error('Failed to export all charts as PDF:', error);
    throw error;
  }
}
